import type {Component} from "vue";
import {MedicationCheckupDefinitionKey} from "@/medication-checkup/interfaces";
import {TreatmentSelectionsView} from "@/medication-checkup/interfaces/treatment-selections";
import {GirerdInterpretation, GirerdView} from "@/medication-checkup/interfaces/girerd";
import {ProbeView} from "@/medication-checkup/interfaces/probe";
import {InterviewSheetView} from "@/medication-checkup/interfaces/interview-sheet";
import {CollectionClosingView} from "@/medication-checkup/interfaces/collection-closing";
import {AlertsView} from "@/medication-checkup/interfaces/alerts";
import {InterventionsView} from "@/medication-checkup/interfaces/interventions";
import {AnalysisClosingView} from "@/medication-checkup/interfaces/analysis-closing";
import {DoctorTransmissionView} from "@/medication-checkup/interfaces/doctor-transmission";
import {CounsellingPlanView} from "@/medication-checkup/interfaces/counselling-plan";
import {CounsellingClosingView} from "@/medication-checkup/interfaces/counselling-closing";
import {FollowUpGirerdView} from "@/medication-checkup/interfaces/follow-up-girerd";
import {FollowUpClosingView} from "@/medication-checkup/interfaces/follow-up-closing";
import {DoctorFeedbackView} from "@/medication-checkup/interfaces/doctor-feedback";
import {NextAppointmentView} from "@/medication-checkup/interfaces/next-appointment";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";
import {pluralize} from "@/core/filters/Pluralize";
import {alertLevelTone} from "@/medication-checkup/services/AlertLevelTones";
import IntroductionScene from "@/medication-checkup/scenes/IntroductionScene.vue";
import TreatmentSelectionsScene from "@/medication-checkup/scenes/TreatmentSelectionsScene.vue";
import MedicationCheckupTreatmentSelectionsChecklist
    from "@/medication-checkup/components/MedicationCheckupTreatmentSelectionsChecklist.vue";
import {useTreatmentSelectionsStore} from "@/medication-checkup/stores/treatment-selections-store";
import PatientProfileScene from "@/medication-checkup/scenes/PatientProfileScene.vue";
import GirerdScene from "@/medication-checkup/scenes/GirerdScene.vue";
import SideEffectsProbeScene from "@/medication-checkup/scenes/SideEffectsProbeScene.vue";
import GalenicProbeScene from "@/medication-checkup/scenes/GalenicProbeScene.vue";
import InterviewSheetScene from "@/medication-checkup/scenes/InterviewSheetScene.vue";
import CollectionClosingScene from "@/medication-checkup/scenes/CollectionClosingScene.vue";
import CollectionAppointmentScene from "@/medication-checkup/scenes/CollectionAppointmentScene.vue";
import AnalysisIntroductionScene from "@/medication-checkup/scenes/AnalysisIntroductionScene.vue";
import AlertsScene from "@/medication-checkup/scenes/AlertsScene.vue";
import InterventionsScene from "@/medication-checkup/scenes/InterventionsScene.vue";
import AnalysisClosingScene from "@/medication-checkup/scenes/AnalysisClosingScene.vue";
import DoctorTransmissionScene from "@/medication-checkup/scenes/DoctorTransmissionScene.vue";
import DoctorFeedbackScene from "@/medication-checkup/scenes/DoctorFeedbackScene.vue";
import CounsellingIntroductionScene from "@/medication-checkup/scenes/CounsellingIntroductionScene.vue";
import CounsellingPlanScene from "@/medication-checkup/scenes/CounsellingPlanScene.vue";
import CounsellingClosingScene from "@/medication-checkup/scenes/CounsellingClosingScene.vue";
import CounsellingAppointmentScene from "@/medication-checkup/scenes/CounsellingAppointmentScene.vue";
import FollowUpIntroductionScene from "@/medication-checkup/scenes/FollowUpIntroductionScene.vue";
import FollowUpGirerdScene from "@/medication-checkup/scenes/FollowUpGirerdScene.vue";
import FollowUpClosingScene from "@/medication-checkup/scenes/FollowUpClosingScene.vue";

/**
 * How a summary reads at a glance: as a plain outcome, a good one, a point
 * to keep an eye on, or an alert.
 */
export type SceneSummaryTone = 'neutral' | 'good' | 'warning' | 'alert';

/**
 * What a completed scene left behind, in one line for the checklist.
 */
export interface SceneSummary {
    label: string;
    tone: SceneSummaryTone;
}

/**
 * A scene of the course: one block of the definition — bubbles, cards and
 * action — rendered by one component.
 *
 * It receives the served block and whether it is frozen (completed): a frozen
 * scene renders the record left behind, a live one animates and submits.
 */
export interface CourseScene {
    key: MedicationCheckupDefinitionKey;
    component: Component;
    /**
     * Whether the checklist of the step lists the scene. Listed unless said
     * otherwise: a scene left out is played in the thread but is not a task of
     * the interview to tick off.
     */
    checklist?: boolean;
    /**
     * The outcome of the scene once its block is completed, read from the
     * frozen view the handler serves — no word here that the record does not
     * already hold, only its shape for the checklist. Without it, the
     * checklist says the scene is done and nothing more.
     */
    summary?: (view: unknown) => SceneSummary | null;
    /**
     * Where the scene stands while it plays, in one line for the checklist —
     * for a scene whose progress is worth more than "En cours".
     */
    ongoing?: () => SceneSummary | null;
    /**
     * What the checklist shows under the task while the scene plays, for a
     * scene whose matter is worth following beside the thread.
     */
    detail?: Component;
}

/**
 * Narrow the served view to the one the scene renders before summarising it.
 */
const summarising = <TView>(read: (view: TView) => SceneSummary | null) => {
    return (view: unknown) => read(view as TView);
};

const treatmentSelectionsSummary = summarising<TreatmentSelectionsView>(view => {
    const retained = view.lines.filter(line => line.confirmed).length;

    return {label: `${retained} ${pluralize('traitement retenu', retained)}`, tone: 'good'};
});

/**
 * The selection under review, counted from the review store — nothing while
 * no review plays.
 */
const treatmentSelectionsOngoing = (): SceneSummary | null => {
    const selection = useTreatmentSelectionsStore();

    if (selection.blockId === null) {
        return null;
    }

    const retained = selection.retained.length;

    return {label: `${retained} ${pluralize('sélectionné', retained)}`, tone: 'neutral'};
};

/**
 * The adherence readings, in the tone the score band of the card gives them.
 */
const GIRERD_TONES: Record<GirerdInterpretation['key'], SceneSummaryTone> = {
    good: 'good',
    minor: 'warning',
    poor: 'alert'
};

const girerdSummary = summarising<GirerdView>(view => {
    if (view.score === null || view.interpretation === null) {
        return null;
    }

    return {
        label: `${view.score}/${view.questions.length} — ${view.interpretation.label}`,
        tone: GIRERD_TONES[view.interpretation.key]
    };
});

/**
 * A probe summarises to what it raised: nothing, or so many findings named
 * the way the probe names them.
 */
const probeSummary = (finding: string) => summarising<ProbeView>(view => {
    if (view.answer === null) {
        return null;
    }

    const count = view.findings.length;

    if (!view.answer || count === 0) {
        return {label: 'Aucun', tone: 'good'};
    }

    return {label: `${count} ${pluralize(finding, count)}`, tone: 'warning'};
});

const interviewSheetSummary = summarising<InterviewSheetView>(view => {
    const answered = view.questions.filter(question => {
        const answer = question.answer;

        return answer !== null && (answer.yes_no !== null || (answer.text ?? '').trim() !== '');
    }).length;

    return {
        label: `${answered}/${view.questions.length} ${pluralize('question renseignée', answered)}`,
        tone: 'good'
    };
});

const collectionClosingSummary = summarising<CollectionClosingView>(view => {
    return {
        label: view.collection_recap === null ? 'Terminée' : 'Récapitulatif PDF généré',
        tone: 'good'
    };
});

/**
 * The alerts summarise to their count, in the tone of the most serious level
 * any source raised.
 */
const alertsSummary = summarising<AlertsView>(view => {
    if (view.run === null) {
        return null;
    }

    const count = view.run.summary.alerts_count;

    if (count === 0) {
        return {label: 'Aucune alerte', tone: 'good'};
    }

    const readings = view.run.sources.flatMap(source => {
        return source.levels.map(level => alertLevelTone(level.severity).reading);
    });

    return {
        label: `${count} ${pluralize('alerte', count)}`,
        tone: readings.includes('alert')
            ? 'alert'
            : readings.includes('warning') ? 'warning' : 'good'
    };
});

/**
 * The intervention summarises to what it proposes to the doctor.
 */
const interventionsSummary = summarising<InterventionsView>(view => {
    if (view.summary === null) {
        return null;
    }

    const count = view.summary.proposals;

    if (count === 0) {
        return {label: 'Aucune proposition', tone: 'good'};
    }

    return {label: `${count} ${pluralize('proposition', count)}`, tone: 'neutral'};
});

/**
 * The synthesis leaves nothing to count of its own: what it passes on to the
 * doctor is the intervention's tally, and the checklist already reads it a
 * line above. Validating it sends nothing: the transmission follows.
 */
const analysisClosingSummary = summarising<AnalysisClosingView>(view => {
    return view.acted === null ? null : {label: 'Synthèse validée', tone: 'good'};
});

/**
 * The posology plan summarises to the changes it hands over to the patient.
 */
const counsellingPlanSummary = summarising<CounsellingPlanView>(view => {
    if (view.acted === null) {
        return null;
    }

    const changes = view.lines.filter(line => line.status !== 'unchanged').length;

    return {
        label: changes === 0 ? 'Aucun changement' : `${changes} ${pluralize('changement', changes)}`,
        tone: 'good'
    };
});

/**
 * The closing of the advice interview summarises to the form it gives.
 */
const counsellingClosingSummary = summarising<CounsellingClosingView>(view => {
    return view.acted === null ? null : {label: 'Entretien conseil PDF généré', tone: 'good'};
});

/**
 * The follow-up Girerd summarises as the collection's does, its score next
 * to the one of the collection.
 */
const followUpGirerdSummary = summarising<FollowUpGirerdView>(view => {
    if (view.score === null || view.interpretation === null) {
        return null;
    }

    const then = view.baseline === null ? '' : ` (recueil : ${view.baseline.score}/${view.questions.length})`;

    return {
        label: `${view.score}/${view.questions.length} — ${view.interpretation.label}${then}`,
        tone: GIRERD_TONES[view.interpretation.key]
    };
});

/**
 * The next appointment summarises to the day it is booked on, read from the
 * agenda — and warns while none is, since nothing then brings the patient
 * back.
 */
const nextAppointmentSummary = summarising<NextAppointmentView>(view => {
    if (view.appointment === null) {
        return {label: 'Aucun rendez-vous prévu', tone: 'warning'};
    }

    return {label: parseAgendaDate(view.appointment.start_at).format('[RDV le] D MMMM [à] HH:mm'), tone: 'good'};
});

/**
 * The closing of the follow-up summarises to the review it closes.
 */
const followUpClosingSummary = summarising<FollowUpClosingView>(view => {
    return view.acted === null ? null : {label: 'Bilan clôturé', tone: 'good'};
});

/**
 * The transmission summarises to the way the analysis reached the doctor.
 */
const doctorTransmissionSummary = summarising<DoctorTransmissionView>(view => {
    if (view.channel === null) {
        return null;
    }

    return {label: view.channel === 'mssante' ? 'Transmise par MSSanté' : 'Transmise directement', tone: 'good'};
});

/**
 * The doctor's answer summarises to what the advice interview will go on
 * with — and warns when the doctor left some of it unsaid, since those
 * proposals stop here. An analysis that proposed nothing had nothing to
 * answer.
 */
const doctorFeedbackSummary = summarising<DoctorFeedbackView>(view => {
    if (view.summary === null) {
        return null;
    }

    if (view.rows.length === 0 && view.initiatives.length === 0) {
        return {label: 'Aucune proposition', tone: 'good'};
    }

    const {retained, unanswered} = view.summary;
    const kept = `${retained} ${pluralize('retenue', retained)}`;

    if (unanswered > 0) {
        return {label: `${kept}, ${unanswered} sans réponse`, tone: 'warning'};
    }

    return {label: kept, tone: 'good'};
});

/**
 * The scenes, in the order of the course.
 */
export const course: CourseScene[] = [
    {key: 'introduction', component: IntroductionScene, checklist: false},
    {
        key: 'treatment_selections',
        component: TreatmentSelectionsScene,
        summary: treatmentSelectionsSummary,
        ongoing: treatmentSelectionsOngoing,
        detail: MedicationCheckupTreatmentSelectionsChecklist
    },
    {key: 'patient_profile', component: PatientProfileScene},
    {key: 'girerd', component: GirerdScene, summary: girerdSummary},
    {key: 'side_effects_probe', component: SideEffectsProbeScene, summary: probeSummary('détecté')},
    {key: 'galenic_probe', component: GalenicProbeScene, summary: probeSummary('problème')},
    {key: 'interview_sheet_habits', component: InterviewSheetScene, summary: interviewSheetSummary},
    {key: 'interview_sheet_physiology', component: InterviewSheetScene, summary: interviewSheetSummary},
    {key: 'interview_sheet_treatments', component: InterviewSheetScene, summary: interviewSheetSummary},
    {key: 'collection_closing', component: CollectionClosingScene, summary: collectionClosingSummary},
    {key: 'collection_appointment', component: CollectionAppointmentScene, summary: nextAppointmentSummary},
    {key: 'analysis_introduction', component: AnalysisIntroductionScene, checklist: false},
    {key: 'alerts', component: AlertsScene, summary: alertsSummary},
    {key: 'interventions', component: InterventionsScene, summary: interventionsSummary},
    {key: 'analysis_closing', component: AnalysisClosingScene, summary: analysisClosingSummary},
    {key: 'doctor_transmission', component: DoctorTransmissionScene, summary: doctorTransmissionSummary},
    {key: 'doctor_feedback', component: DoctorFeedbackScene, summary: doctorFeedbackSummary},
    {key: 'counselling_introduction', component: CounsellingIntroductionScene, checklist: false},
    {key: 'counselling_plan', component: CounsellingPlanScene, summary: counsellingPlanSummary},
    {key: 'counselling_closing', component: CounsellingClosingScene, summary: counsellingClosingSummary},
    {key: 'counselling_appointment', component: CounsellingAppointmentScene, summary: nextAppointmentSummary},
    {key: 'follow_up_introduction', component: FollowUpIntroductionScene, checklist: false},
    {key: 'follow_up_girerd', component: FollowUpGirerdScene, summary: followUpGirerdSummary},
    {key: 'follow_up_closing', component: FollowUpClosingScene, summary: followUpClosingSummary},
];
